import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Spin, Table } from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { adminContext } from "../../Common/PageRoute";
import AddPractice from "./AddPractices";
import PracticesService, {
  PracticeStatus,
} from "../../Services/PracticesService";
import DailyPracticeStats from "../../Services/DailyPracticeStats";
import { useUser } from "../../Context/UserContext";
import {
  PageLayout,
  PageHeader,
  SectionCard,
  TableToolbar,
  EmptyState,
  FilterCriteria,
} from "../../Common/Layout";
import { DailyPracticeRecord } from "./types";
import PracticeQuickDrawer from "./PracticeQuickDrawer";
import usePracticeManagementColumns, {
  PracticeManagementRecord,
  formatUserName,
} from "./usePracticeManagementColumns";

interface SortState {
  sortCol: string;
  sortDir: "ascend" | "descend";
}

interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
}

const DEFAULT_SORT: SortState = {
  sortCol: "name",
  sortDir: "ascend",
};

const DEFAULT_PAGINATION: PaginationState = {
  currentPage: 1,
  pageSize: 15,
  totalRecords: 0,
};

export const Practices: React.FC = () => {
  const { openNotification } = React.useContext(adminContext);
  const { isSubAdmin } = useUser();
  const isViewOnly = isSubAdmin();

  // Data state
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [practiceData, setPracticeData] = useState<{
    items: PracticeManagementRecord[];
    totalRecords: number;
  }>({
    items: [],
    totalRecords: 0,
  });

  // Modal dialog state
  const [activeModal, setActiveModal] = useState<"addPractice" | null>(null);

  // Inspection drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<DailyPracticeRecord | null>(null);

  // Sorting & pagination state
  const [currentSort, setCurrentSort] = useState<SortState>(DEFAULT_SORT);
  const [pagination, setPagination] =
    useState<PaginationState>(DEFAULT_PAGINATION);

  // Filters state
  const [filters, setFilters] = useState<{
    search: string;
    status?: string;
  }>({
    search: "",
    status: undefined,
  });

  const fetchPractices = useCallback(
    async (
      page: number = pagination.currentPage,
      size: number = pagination.pageSize,
      sortCol: string = currentSort.sortCol,
      sortDir: "ascend" | "descend" = currentSort.sortDir,
    ) => {
      setLoading(true);
      try {
        const start = (page - 1) * size;
        const statusParam =
          filters.status !== undefined && filters.status !== ""
            ? (parseInt(filters.status, 10) as PracticeStatus)
            : undefined;

        const res: any = await PracticesService.getPracticesList(
          start,
          size,
          filters.search?.trim() || "",
          sortCol,
          sortDir,
          statusParam,
        );

        const items: PracticeManagementRecord[] =
          res?.result?.items || res?.items || (Array.isArray(res) ? res : []);
        const totalRecords: number =
          res?.result?.totalRecords ?? res?.totalRecords ?? items.length;

        setPracticeData({ items, totalRecords });
        setPagination((prev) => ({
          ...prev,
          currentPage: page,
          pageSize: size,
          totalRecords,
        }));
      } catch (error) {
        console.error("Failed to fetch practices:", error);
        openNotification(
          "error",
          "Failed to load practices. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      currentSort.sortCol,
      currentSort.sortDir,
      filters.search,
      filters.status,
      openNotification,
      pagination.currentPage,
      pagination.pageSize,
    ],
  );

  useEffect(() => {
    fetchPractices(
      1,
      pagination.pageSize,
      currentSort.sortCol,
      currentSort.sortDir,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, refreshKey]);

  const handleTableChange = (
    tablePagination: any,
    _filters: any,
    sorter: any,
  ) => {
    let sortCol = DEFAULT_SORT.sortCol;
    let sortDir: "ascend" | "descend" = "ascend";

    if (sorter?.order) {
      sortDir = sorter.order === "descend" ? "descend" : "ascend";
    }

    if (sorter?.field) {
      let field = sorter.field;
      if (Array.isArray(field)) field = field.join(",");
      if (typeof field === "string" && field.includes(",")) {
        sortCol = field.split(",")[1];
      } else {
        sortCol = field;
      }
    }

    const newPage = tablePagination.current || 1;
    const newPageSize = tablePagination.pageSize || 15;

    setCurrentSort({ sortCol, sortDir });
    fetchPractices(newPage, newPageSize, sortCol, sortDir);
  };

  const handleOpenQuickDrawer = useCallback(
    async (record: PracticeManagementRecord) => {
      const displayName =
        formatUserName(record.name) || `Practice #${record.id}`;
      try {
        const res: any = await DailyPracticeStats.getDailyPracticeStats({
          start: 0,
          length: 10,
          search: record.id.toString(),
        });
        const items: DailyPracticeRecord[] =
          res?.result?.items || res?.items || (Array.isArray(res) ? res : []);
        const matching =
          items.find((i) => i.practiceId === record.id) || items[0];
        if (matching) {
          setSelectedRecord(matching);
        } else {
          setSelectedRecord({
            practiceId: record.id,
            practiceName: displayName,
            stats: {} as any,
          });
        }
      } catch (e) {
        setSelectedRecord({
          practiceId: record.id,
          practiceName: displayName,
          stats: {} as any,
        });
      }
      setDrawerOpen(true);
    },
    [],
  );

  const handleCopyId = useCallback(
    (id: number) => {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(id.toString());
        openNotification("info", `Practice ID #${id} copied to clipboard`);
      }
    },
    [openNotification],
  );

  const { columns, components, columnSelector } = usePracticeManagementColumns({
    currentPage: pagination.currentPage,
    pageSize: pagination.pageSize,
    currentSort,
    onOpenQuickDrawer: handleOpenQuickDrawer,
    onCopyId: handleCopyId,
  });

  const filterCriteria: FilterCriteria[] = useMemo(
    () => [
      {
        key: "status",
        label: "Status",
        type: "select",
        placeholder: "Select status",
        options: [
          { label: "Active", value: "0" },
          { label: "Inactive", value: "1" },
        ],
      },
    ],
    [],
  );

  const activeFilters = useMemo(
    () => ({
      status: filters.status,
    }),
    [filters.status],
  );

  const handleFiltersChange = useCallback((newFilters: Record<string, any>) => {
    setFilters((prev) => ({
      ...prev,
      status: newFilters.status,
    }));
  }, []);

  const pageHeaderActions = useMemo(() => {
    if (isViewOnly) return [];
    return [
      <Button
        key="add-practice"
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setActiveModal("addPractice")}
      >
        Add Practice
      </Button>,
    ];
  }, [isViewOnly]);

  return (
    <>
      <PageLayout maxWidth="100%">
        <PageHeader
          title="Practices"
          subtitle="Manage tenant practices, configurations, features, and system access"
          actions={pageHeaderActions}
        />

        {/* Practice Management Table */}
        <SectionCard noPadding>
          <div style={{ padding: "6px 14px" }}>
            <TableToolbar
              onSearch={(val) =>
                setFilters((prev) => ({ ...prev, search: val }))
              }
              searchPlaceholder="Search practice name, email or phone..."
              filterCriteria={filterCriteria}
              activeFilters={activeFilters}
              onFiltersChange={handleFiltersChange}
              onRefresh={() => setRefreshKey((k) => k + 1)}
              extra={columnSelector}
            />
          </div>

          <Table
            dataSource={practiceData.items}
            columns={columns}
            components={components}
            rowKey="id"
            size="small"
            tableLayout="fixed"
            loading={{
              spinning: loading,
              indicator: <Spin indicator={<LoadingOutlined />} />,
            }}
            className="border-top-table"
            scroll={{
              x: "max-content",
              y: "calc(100vh - 300px)",
            }}
            locale={{
              emptyText: (
                <EmptyState
                  title="No practices found"
                  description="Try adjusting your search query or status filter"
                />
              ),
            }}
            pagination={{
              current: pagination.currentPage,
              pageSize: pagination.pageSize,
              total: practiceData.totalRecords,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} practices`,
              showSizeChanger: true,
              pageSizeOptions: ["15", "25", "50", "100"],
            }}
            onChange={handleTableChange}
          />
        </SectionCard>
      </PageLayout>

      {/* Slide-over Inspection Drawer */}
      <PracticeQuickDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
      />

      {/* Add Practice Modal */}
      {activeModal === "addPractice" && (
        <AddPractice
          onDismiss={() => {
            setActiveModal(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </>
  );
};

export default Practices;
